import querystring from "querystring";

import {
  IsoLocale,
  UserRoles,
  IS_SUPER_ADMIN_QUERY_PARAM,
  RoutePrefix,
  Emptyable,
  Nullable,
  UserPermissions,
  PublicRoutes,
  getDefaultDonorRoute,
  getDefaultOrganizationRoute,
} from "@simplyk/common";
import router from "next/router";

import { FRONTEND_URL } from "../routes/routes";

import { View } from "@/enums/view";

export const LOGIN_QUERY_PARAM_INVALID_EMAIL_FOR_GOOGLE_SSO = "invalid_email_for_google_sso";
export const LOGIN_QUERY_PARAM_AUTHENTICATION_FAILED = "authentication_failed";

const reloadOrRedirect = (redirectionUrl: string, reload = false) => {
  if (reload) {
    window.location.href = redirectionUrl;
  } else {
    router.push(redirectionUrl);
  }
};

export const getDashboardReloadUrl = (path: string, isoLocale?: IsoLocale) =>
  `${FRONTEND_URL}${isoLocale ? "/" + isoLocale : ""}${path}`;

export const setAuthAndRedirectToApp = ({
  role,
  redirectionUrl,
  lastUserView,
  reload = false,
  isoLocale,
  userPermissions,
}: {
  role: Emptyable<UserRoles>;
  redirectionUrl?: Nullable<string>;
  lastUserView?: View;
  reload?: boolean;
  isoLocale?: IsoLocale;
  userPermissions: Emptyable<UserPermissions[]>;
}) => {
  const defaultDonorRoute = getDefaultDonorRoute();
  const defaultOrganizationRoute = getDefaultOrganizationRoute(userPermissions);
  if (redirectionUrl) {
    reloadOrRedirect(redirectionUrl, reload);
  } else if (lastUserView === View.Donor || role === UserRoles.Donor) {
    reloadOrRedirect(reload ? getDashboardReloadUrl(defaultDonorRoute, isoLocale) : defaultDonorRoute, reload);
  } else if (role === UserRoles.Owner || role === UserRoles.Member) {
    reloadOrRedirect(
      reload ? getDashboardReloadUrl(defaultOrganizationRoute, isoLocale) : defaultOrganizationRoute,
      reload
    );
  }
};

export const isOrganizationRoute = (path?: string) => path?.includes(`${RoutePrefix.Organization}/`);
export const isDonorRoute = (path?: string) => path?.includes(`${RoutePrefix.Donor}/`);
export const isAdminRoute = (path?: string) => path?.includes(`/admin/`) && !path?.includes("admin/login"); // Admin login is a public page as we are not yet logged in

export const isPublicRoute = (path?: string) => {
  return (
    (!path?.includes(`${RoutePrefix.Donor}/`) && !path?.includes(`${RoutePrefix.Organization}/`)) ||
    // this case is to handle e-ticket QR codes before login migration
    path?.includes(`${RoutePrefix.Donor}/ticket/`)
  );
};

export const isPrivateRoute = (path?: string) => {
  return !isPublicRoute(path);
};

export const redirectAuthenticatedUserToLogout = ({
  connectedAsAdministrator,
}: {
  connectedAsAdministrator: boolean;
}) => {
  // * Checking if router exists for serverside rendering cases
  if (!router || (!isOrganizationRoute(router.pathname) && !isDonorRoute(router.pathname))) {
    return;
  }
  const query = querystring.stringify({
    [IS_SUPER_ADMIN_QUERY_PARAM]: connectedAsAdministrator ? true : false,
  });

  return router.push(`${PublicRoutes.SignOut}?${query}`);
};

export const redirectToLogout = (options: { connectedAsAdministrator: boolean; redirectionUrl?: string }) => {
  if (!router || isPublicRoute(router.pathname)) {
    return;
  }

  const redirectionUrl = options?.redirectionUrl;
  const query = querystring.stringify({
    [IS_SUPER_ADMIN_QUERY_PARAM]: options.connectedAsAdministrator ? true : false,
    redirectionUrl,
  });

  return router.push(`${PublicRoutes.SignOut}?${query}`);
};
